import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';

import {
    afterRenderEffect,
    AfterViewInit,
    booleanAttribute,
    Component,
    computed,
    ElementRef,
    EventEmitter,
    inject,
    Injector,
    input,
    OnDestroy,
    OnInit,
    signal,
    untracked,
    viewChild,
    ChangeDetectionStrategy,
} from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { FuseScrollbarDirective } from '@fuse/directives/scrollbar';
import { LoadingComponent } from '@kiwid-app/kiwid-ui-lib-angular';
import { KVideoComponent } from '@kiwid-app/kiwid-ui-lib-angular/k-video';
import { FileType, KStlModelViewerComponent } from '@kiwid-app/kiwid-ui-lib-angular/k-stl-model-viewer';
import { EMediaType } from 'app/core/enums/media-type.enum';
import { FileHelper } from 'app/core/helpers/file.helper';
import { IMedia } from 'app/core/models/media/media';
import { NgxTippyModule } from 'ngx-tippy-wrapper';
import { catchError, from, of, Subscription } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { switchMap } from 'rxjs/operators';
import heic2any from 'heic2any';

@Component({
    selector: 'render-media',
    templateUrl: './render-media.component.html',
    styleUrls: ['./render-media.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [NgxTippyModule, MatIconModule, FuseScrollbarDirective, KStlModelViewerComponent, LoadingComponent, KVideoComponent],
})
export class RenderMediaComponent implements OnInit, OnDestroy, AfterViewInit {
    private readonly domSanitizer = inject(DomSanitizer);
    private readonly breakpointObserver = inject(BreakpointObserver);
    private readonly injector = inject(Injector);
    private readonly http = inject(HttpClient);

    public readonly media = input<IMedia | null>(null);
    public readonly file = input<File | null>(null);

    protected readonly subscriptions = new Subscription();
    protected readonly model3DUrl = signal<string | SafeUrl>('');
    protected readonly model3DFileType = signal<FileType>(FileType.STL);
    protected readonly mediaUrl = signal<string | SafeUrl>(null);
    protected readonly is3DModel = computed(() => FileHelper.is3DModel(this.fileName()));
    protected readonly isPDF = computed(() => FileHelper.isPDF(this.fileName()));
    protected readonly isRaw = computed(() => {
        const media = this.media();
        return media?.type === EMediaType.RAW || FileHelper.isRaw(this.fileName());
    });
    protected readonly isImage = computed(() => {
        if (this.isRaw()) {
            return false;
        }

        if (!!this.file()) {
            return FileHelper.isImage(this.fileName());
        }

        return this.media()?.type === EMediaType.IMAGE;
    });
    protected readonly fileName = computed(() => {
        const file = this.file();

        if (!!file) {
            return file.name;
        }

        const media = this.media();
        if (!!media) {
            return media.originalName ?? '';
        }

        return '';
    });
    protected readonly isIframe = computed(() => FileHelper.isHtml3DModelOrUrl(this.fileName()));
    protected readonly placeholderAudio = computed(() => this.media()?.thumbnail ?? '/assets/images/media/audio-thumbnail.jpg');
    protected readonly isVideo = computed(() => {
        if (this.isRaw()) {
            return false;
        }

        if (!!this.file()) {
            return FileHelper.isVideo(this.fileName());
        }

        return this.media()?.type === EMediaType.VIDEO;
    });
    protected readonly isAudio = computed(() => {
        if (this.isRaw()) {
            return false;
        }

        if (!!this.file()) {
            return FileHelper.isAudio(this.fileName());
        }

        return this.media()?.type === EMediaType.AUDIO;
    });
    public readonly enableRotate = input(false, {
        transform: (value: boolean) => (this.media()?.type === EMediaType.IMAGE ? value : false),
    });
    public readonly enableZoomPhoto = input(false, { transform: booleanAttribute });
    public readonly maxHeight = input<string | null>(null);
    public readonly enableMaxHeight = input(false);
    readonly photo = viewChild<ElementRef<HTMLImageElement>>('photo');
    readonly mainPhoto = viewChild<ElementRef>('mainPhoto');
    readonly currentAngleObservable = input<EventEmitter<number>>(new EventEmitter<number>());
    protected readonly rotateString = signal('');
    protected readonly angleValue = signal(0);
    protected readonly photoOriginalSize = signal<{ width: number; height: number } | null>(null);
    protected readonly photoScaled = signal(1);
    private photoPos = { top: 0, left: 0, x: 0, y: 0 };
    private mouseMove: any;
    private mouseDown: any;
    private mouseUp: any;
    private readonly refIframePdf = viewChild<ElementRef<HTMLIFrameElement>>('iframePdf');
    protected readonly isLoadingIframePdf = signal(true);
    protected readonly isLoading = signal(true);
    protected readonly isDeviceMobile = signal(false);

    constructor() {
        this.breakpointObserver.observe([Breakpoints.HandsetPortrait, Breakpoints.HandsetLandscape]).subscribe((result) => {
            this.isDeviceMobile.set(result.matches);
        });

        afterRenderEffect(() => {
            const media = this.media();
            if (!media) {
                return;
            }

            untracked(() => {
                this.mediaUrl.set('');
                setTimeout(() => {
                    if (this.isAudio()) {
                        this.mediaUrl.set(media.url);
                        return;
                    }

                    this.mediaUrl.set(this.domSanitizer.bypassSecurityTrustResourceUrl(media.url));
                }, 100);

                if (!!media && this.isRaw() && this.is3DModel()) {
                    this.model3DFileType.set(FileHelper.get3DModelType(media.url));
                    this.model3DUrl.set('');
                    setTimeout(() => {
                        this.model3DUrl.set(media.url);
                    }, 100);
                }
            });
        });

        afterRenderEffect(() => {
            const file = this.file();
            untracked(() => {
                if (!!file) {
                    setTimeout(() => {
                        if (this.isAudio()) {
                            this.mediaUrl.set(window.URL.createObjectURL(file));
                            return;
                        }
                        this.mediaUrl.set(this.domSanitizer.bypassSecurityTrustResourceUrl(window.URL.createObjectURL(file)));
                    }, 100);

                    if (this.is3DModel()) {
                        this.model3DFileType.set(FileHelper.get3DModelType(file.name));
                        const reader = new FileReader();
                        reader.readAsDataURL(file);
                        reader.onload = () => {
                            this.model3DUrl.set('');
                            setTimeout(() => {
                                this.model3DUrl.set(reader.result);
                            }, 100);
                        };
                    }
                }
            });
        });

        afterRenderEffect(() => {
            const refIframePdf = this.refIframePdf();

            untracked(() => {
                if (!!refIframePdf) {
                    this.isLoadingIframePdf.set(true);
                    refIframePdf.nativeElement.addEventListener('load', () => {
                        this.isLoadingIframePdf.set(false);
                    });
                } else {
                    this.isLoadingIframePdf.set(false);
                }
            });
        });

        let interval = null;
        afterRenderEffect((onCleanup) => {
            const isLoadingIframePdf = this.isLoadingIframePdf();

            clearInterval(interval);

            untracked(() => {
                const refIframeRef = this.refIframePdf();
                if (!!refIframeRef && isLoadingIframePdf) {
                    if (!refIframeRef.nativeElement.src) {
                        refIframeRef.nativeElement.src = `https://docs.google.com/viewer?url=${this.media()?.url}&embedded=true`;
                    }

                    let totalRetry = 0;
                    interval = setInterval(() => {
                        totalRetry += 1;
                        refIframeRef.nativeElement.src = `https://docs.google.com/viewer?url=${this.media()?.url}&embedded=true`;

                        if (totalRetry > 10) {
                            clearInterval(interval);
                        }
                    }, 2000);
                }
            });

            onCleanup(() => clearInterval(interval));
        });

        afterRenderEffect(async () => {
            const file = this.file();
            const media = this.media();
            const fileName = this.fileName();

            untracked(() => {
                this.isLoading.set(false);

                if (FileHelper.isImage(fileName) && FileHelper.isHeic(fileName)) {
                    this.isLoading.set(true);

                    if (!!media) {
                        this.http
                            .get(media.url, { responseType: 'blob' })
                            .pipe(
                                switchMap((blob) =>
                                    from(
                                        heic2any({
                                            blob: blob,
                                            toType: 'image/jpeg',
                                            quality: 0.8,
                                        }),
                                    ),
                                ),
                                catchError((error) => {
                                    return of(null);
                                }),
                            )
                            .subscribe({
                                next: (convertedBlob) => {
                                    if (convertedBlob) {
                                        const convertedFile = new File([convertedBlob as Blob], media.originalName.replace(/\.heic$/i, '.jpg'), {
                                            type: 'image/jpeg',
                                        });
                                        this.mediaUrl.set(window.URL.createObjectURL(convertedFile));
                                    }
                                    this.isLoading.set(false);
                                },
                                error: (error) => {
                                    this.isLoading.set(false);
                                },
                            });

                        return;
                    }

                    heic2any({
                        blob: file,
                        toType: 'image/jpeg',
                        quality: 1,
                    })
                        .then((convertedBlob) => {
                            if (convertedBlob) {
                                const convertedFile = new File([convertedBlob as Blob], fileName.replace(/\.heic$/i, '.jpg'), { type: 'image/jpeg' });
                                this.mediaUrl.set(window.URL.createObjectURL(convertedFile));
                            }

                            this.isLoading.set(false);
                        })
                        .then((error) => {
                            this.isLoading.set(false);
                        });
                }
            });
        });
    }

    ngOnInit(): void {
        this.mouseDown = this.mouseDownHandler.bind(this);
        this.mouseMove = this.mouseMoveHandler.bind(this);
        this.mouseUp = this.mouseUpHandler.bind(this);
    }

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }

    ngAfterViewInit(): void {
        this.resetImageZoom();
    }

    protected resetImageZoom(): void {
        const photo = this.photo();
        if (!!photo) {
            photo.nativeElement.style.width = 'auto';
            photo.nativeElement.style.height = 'auto';

            this.photoOriginalSize.set({
                width: photo.nativeElement.clientWidth,
                height: photo.nativeElement.clientHeight,
            });

            const elem = this.mainPhoto().nativeElement;
            const middleOffset = photo.nativeElement.offsetTop;
            const middleOffsetLeft = photo.nativeElement.offsetLeft;
            elem.scrollTop = middleOffset - elem.offsetHeight / 2;
            elem.scrollLeft = middleOffsetLeft - elem.offsetWidth / 2;

            this.mainPhoto().nativeElement.addEventListener('mousedown', this.mouseDown);
            document.removeEventListener('mousemove', this.mouseMove);
            document.removeEventListener('mouseup', this.mouseUp);
        }
        this.photoScaled.set(1);
    }

    private mouseDownHandler(e): void {
        // Get the current mouse position
        this.photoPos = {
            // The current scroll
            left: this.mainPhoto().nativeElement.scrollLeft,
            top: this.mainPhoto().nativeElement.scrollTop,
            // Get the current mouse position
            x: e.clientX,
            y: e.clientY,
        };

        // Attach the listeners to `document`
        document.addEventListener('mousemove', this.mouseMove);
        document.addEventListener('mouseup', this.mouseUp);
    }

    private mouseMoveHandler(e): void {
        // How far the mouse has been moved
        const dx = e.clientX - this.photoPos.x;
        const dy = e.clientY - this.photoPos.y;

        // Scroll the element
        this.mainPhoto().nativeElement.scrollTop = this.photoPos.top - dy;
        this.mainPhoto().nativeElement.scrollLeft = this.photoPos.left - dx;
    }

    private mouseUpHandler(e): void {
        document.removeEventListener('mousemove', this.mouseMove);
        document.removeEventListener('mouseup', this.mouseUp);

        this.mainPhoto().nativeElement.style.cursor = 'grab';
        this.mainPhoto().nativeElement.style.removeProperty('user-select');
    }

    protected rotateImage(): void {
        const angle = this.angleValue() + 90;
        this.angleValue.set(angle === 360 ? 0 : angle);
        this.rotateString.set(`rotate(${this.angleValue()}deg)`);
        this.currentAngleObservable().emit(this.angleValue());
    }

    protected zoom(type: 'in' | 'out'): void {
        if (type === 'in' && this.photoScaled() < 5) {
            this.photoScaled.set(this.photoScaled() + 0.25);
        } else if (type === 'out' && this.photoScaled() > 1) {
            this.photoScaled.set(this.photoScaled() - 0.25);
        }

        this.photo().nativeElement.style.width = `${this.photoOriginalSize().width * this.photoScaled()}px`;
        this.photo().nativeElement.style.height = `auto`;
        // this.photo.nativeElement.style.height = `${this.photoOriginalSize.height * this.photoScaled}px`;

        const elem = this.mainPhoto().nativeElement;
        const middleOffset = this.photo().nativeElement.offsetTop;
        const middleOffsetLeft = this.photo().nativeElement.offsetLeft;
        elem.scrollTop = middleOffset - elem.offsetHeight / 2;
        elem.scrollLeft = middleOffsetLeft - elem.offsetWidth / 2;
    }
}

export type IMediaRender = File | IMedia;
